import React from 'react';
import {
  find,
  get,
  groupBy,
  has,
  identity,
  includes,
  isEmpty,
  isEqual,
  isUndefined,
  keys,
  map,
  range,
  reduce,
  sortBy,
  startsWith,
  trim,
  values,
} from 'lodash';
import classnames from 'classnames';
import cx from 'classnames';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  formControlLabelClasses,
  InputBase,
  inputBaseClasses,
  styled,
} from '@mui/material';
import Constants from '../../../Constants';
import { FORM_TYPE_CREATE_DATA_SOURCE } from 'js/constants/formTypes';
import DebouncedText from '../../elements/editors/mui/DebouncedText';
import { Button } from 'new/elements/support/Button';
import LoadableMonacoEditor from '../../elements/editors/monaco/LoadableMonacoEditor';
import { getEditorLanguage } from '../../elements/editors/monaco/MonacoEditorUtils';
import ParameterizedURLPreview from './ParameterizedURLPreview';
import DataPathSuggestions from './DataPathSuggestions';
import { formatCode, stringifyObject } from 'js/lib/FormatUtils';
import UIUtils from 'js/lib/UIUtils';
import MentionsBasicEditor from '../../elements/editors/draftjs/MentionsBasicEditor';
import {
  getCurrentSelectedOption,
  getNewSelectionValue,
  redesignStyles,
  SelectInput,
  SelectOption,
} from '../../elements/rselect/ReactSelectUtils';
import SortedSelect from '../../elements/rselect/SortedSelect';
import UnsortedSelect from '../../elements/rselect/UnsortedSelect';
import { ReactComponent as RefreshIconRotate } from 'new/components/icons/fi-rr-refresh-rotate.svg';
import {
  convertTextToMentionsState,
  DEFAULT_MACRO_ENTITY_NAME,
  extractPayloadFromMentionContent,
} from 'js/lib/DraftJSUtils';
import Tooltip from '../../elements/mui/Tooltip';
import { MarkdownContent } from 'new/elements/MarkdownContent';
import { ReactComponent as Expand } from 'js/components/elements/treeview/assets/expandMoreFilled.svg';
import { ReactComponent as InfoIcon } from 'new/components/icons/fi-rr-info.svg';
import { HeaderField } from 'new/elements/FieldBuilderRedesigned/FieldBuilder';
import { MacroEditor, MacroEditorConfigProvider } from 'new/elements/MacroEditor';
import {
  extractMacrosFromApiServerFlow,
  extractDatasetFromApiServerFlow,
} from 'js/lib/MacroReplacementUtils';
import { FLOW_TYPE_API_SERVER } from 'js/constants/pipelineConstants';

const SELECT_FIELD_CUSTOM_STYLES = {
  baseControl: {
    backgroundColor: '#fff',
  },
  fixedWidthControl: {
    backgroundColor: '#fff',
    width: 100,
  },
};

const customPrefixSystemMacros = [
  {
    name: '{now}',
    display_name: 'Now',
    type: 'def',
    type_display: 'System Macros',
  },
  {
    name: '{now-1}',
    display_name: 'Now - 1 Time Unit',
    type: 'def',
    type_display: 'System Macros',
  },
  {
    name: '{now+1}',
    display_name: 'Now + 1 Time Unit',
    type: 'def',
    type_display: 'System Macros',
  },
];

const replaceMacroMap = (macroGroups = []) => {
  return reduce(
    macroGroups,
    (acc, g) => {
      // Special handling for 'attr' type (Data Attributes from parent dataset)
      // No prefix needed as the macro already includes the full path (e.g., {path_params.customerID})
      if (g === 'attr') {
        acc[g] = {
          prefix: '{',
          suffix: '}',
        };
      } else {
        acc[g] = {
          prefix: `{${g}.`,
          suffix: '}',
        };
      }
      return acc;
    },
    {
      grouped: true,
      def: {
        prefix: '{',
        suffix: '}',
      },
      default: {
        prefix: '{',
        suffix: '}',
      },
      attr: {
        prefix: '{',
        suffix: '}',
      },
    },
  );
};

export const getMentionsFromMacroAttrs = (props) => {
  const { stageIndex, apiURLMacros, parentDataSet, parentDataSetSamples, editedSamples } = props;

  const includeStageKeys = map(
    range(stageIndex),
    (id) => `${Constants.IterationPrefixes.STAGE}${id + 1}`,
  );

  // Get macros from API responses (previous stages)
  let macrosList = reduce(
    apiURLMacros,
    (acc, g, k) => {
      if (startsWith(k, Constants.IterationPrefixes.STAGE) && !includes(includeStageKeys, k)) {
        return acc;
      } else {
        return [...acc, ...g];
      }
    },
    customPrefixSystemMacros,
  );

  // Add parent dataset attribute macros for api_server flows
  // This extracts nested keys like {path_params.id}, {query_params.status}, etc.
  const parentDataSetMacros = extractMacrosFromApiServerFlow(
    parentDataSet,
    parentDataSetSamples,
    editedSamples,
  );

  if (!isEmpty(parentDataSetMacros)) {
    macrosList = [...macrosList, ...parentDataSetMacros];
  }

  let sortedMacrosList = sortBy(macrosList, ['type']);
  let macroGroups = [...keys(apiURLMacros)];

  // Add 'attr' to macro groups if parent dataset macros exist
  if (!isEmpty(parentDataSetMacros)) {
    macroGroups.push('attr');
  }

  return { macros: sortedMacrosList, macroMap: replaceMacroMap(macroGroups) };
};

const getStartingEditorState = (props, stageURLMacros = [], macroMap = {}) => {
  const { stageAPIConfig } = props;

  let startURL = has(stageAPIConfig, `url.template`) ? stageAPIConfig['url.template'] : 'http://';

  if (stageURLMacros !== null && !isEmpty(stageURLMacros)) {
    //use selected key from
    return convertTextToMentionsState(
      startURL,
      stageURLMacros,
      DEFAULT_MACRO_ENTITY_NAME,
      macroMap,
    );
  } else {
    return convertTextToMentionsState(startURL, null);
  }
};

const _getStateFromProps = (props) => {
  const { macros: stageMacros, macroMap } = getMentionsFromMacroAttrs(props);
  const urlTemplateEditorState = getStartingEditorState(props, stageMacros, macroMap);

  return {
    urlTemplateEditorState,
    stageURLMacros: stageMacros,
    macroMap,
    filteredURLSuggestions: stageMacros,
    filteredBodySuggestions: stageMacros,
  };
};

const muiStyles = {
  checkboxIcon: {
    root: {
      color: '#4AD496',
      '&.Mui-checked': {
        color: '#4AD496',
      },
    },
    checked: {},
  },
  checkboxLabel: {
    root: {},
    label: {
      fontSize: 12,
      fontWeight: 500,
      color: '#1F2F40',
      lineHeight: '24px',
      textTransform: 'initial',
      letterSpacing: '.5px',
    },
  },
  inputBase: {
    root: {
      width: '100%',
      display: 'block',
      border: '1px solid #DFE8F0',
      borderRadius: 2,
      lineHeight: 1.78571,
      fontSize: 12,
      fontWeight: 400,
      color: '#1F2F40',
    },
    input: {
      padding: '0 10px',
    },
  },
};

const StyledFormControlLabel = styled(FormControlLabel)({
  [`.${formControlLabelClasses.label}`]: muiStyles.checkboxLabel.label,
});
const StyledCheckbox = styled(Checkbox)(muiStyles.checkboxIcon.root);
const StyledInputBase = styled(InputBase)({
  ...muiStyles.inputBase.root,
  [`.${inputBaseClasses.input}`]: muiStyles.inputBase.input,
});

const FieldLabelView = ({ fieldName, fieldsByName, defaultLabel = '', labelClassName = '' }) => {
  const fieldInfo = !isEmpty(fieldsByName[fieldName]) ? fieldsByName[fieldName][0] : null;

  if (fieldInfo) {
    return <HeaderField item={fieldInfo} className={labelClassName} />;
  } else {
    return (
      <label className={labelClassName} htmlFor={fieldName}>
        {defaultLabel}
      </label>
    );
  }
};

class ConfigureAPIStage extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      ..._getStateFromProps(props),
      activePanelKey: 1,
    };

    this._handleCallAPIClicked = this._handleCallAPIClicked.bind(this);
    this._handleCallAPIKeyDown = this._handleCallAPIKeyDown.bind(this);
    this._handleLookupSelectionChanged = this._handleLookupSelectionChanged.bind(this);
    this._getPathMatches = this._getPathMatches.bind(this);
    this._getIterationTypeFields = this._getIterationTypeFields.bind(this);
    this._getDataPathField = this._getDataPathField.bind(this);
    this._getTextField = this._getTextField.bind(this);
    this._getSelectField = this._getSelectField.bind(this);

    this._handleURLKeyDown = this._handleURLKeyDown.bind(this);
    this._handleURLTemplateEditorChange = this._handleURLTemplateEditorChange.bind(this);
    this._handleURLTemplateEditorSearch = this._handleURLTemplateEditorSearch.bind(this);
    this._handleDataPathSuggestionClicked = this._handleDataPathSuggestionClicked.bind(this);
    this._handlePanelSelected = this._handlePanelSelected.bind(this);
  }

  componentDidUpdate(prevProps) {
    const {
      stageAPIConfig,
      stageIndex,
      apiResponse,
      validateDataPath,
      parentDataSetSamples,
      parentDataSet,
    } = this.props;
    //validate data path if the
    // there is change in iteration_type,
    const { 'url.template': nextUrlTemplate, ...newConfig } = stageAPIConfig;
    const { 'url.template': prevUrlTemplate, ...prevConfig } = prevProps.stageAPIConfig;
    const { apiResponse: prevApiResponse } = prevProps;

    let shouldValidatePath = false;

    //Deep comparision can be usually expensive but stage config is a shallow object.
    //Need this to ensure that validations are fired only if relevant config params changed (added/removed/modified)
    //or when the response content changed(added removed or modified).
    if (!isEqual(newConfig, prevConfig) && apiResponse && apiResponse.apiResponseContent) {
      shouldValidatePath = true;
    } else if (
      apiResponse &&
      ((!prevApiResponse && apiResponse.apiResponseContent) ||
        (prevApiResponse && apiResponse.apiResponseContent !== prevApiResponse.apiResponseContent))
    ) {
      shouldValidatePath = true;
    }

    if (shouldValidatePath === true) {
      validateDataPath(stageAPIConfig, stageIndex);
    }

    // Check if macrosUpdateTime changed OR if parentDataSetSamples changed OR if editedSamples changed
    const macrosUpdateTimeChanged = this.props.macrosUpdateTime !== prevProps.macrosUpdateTime;
    const parentDataSetSamplesChanged = parentDataSetSamples !== prevProps.parentDataSetSamples;
    const parentDataSetChanged = parentDataSet !== prevProps.parentDataSet;
    const editedSamplesChanged = this.props.editedSamples !== prevProps.editedSamples;

    if (macrosUpdateTimeChanged || parentDataSetSamplesChanged || editedSamplesChanged) {
      //TODO: changing this in cwrp was breaking dropdowns because macros update time is updated as soon as component is mounted.
      let { filteredURLSuggestions, filteredBodySuggestions, stageURLMacros, macroMap } =
        _getStateFromProps(this.props);
      //TODO: not updating editor state here. Somehow it was breaking the dropdown render.
      //Ideally editor mentions should be recomputed if the available suggestions changed
      this.setState({
        filteredURLSuggestions,
        filteredBodySuggestions,
        stageURLMacros,
        macroMap,
      });
    }
  }

  render() {
    const {
      paging: { sourceConfigById },
      apiConfig,
      editedSamples = null,
      stageAPIConfig,
      stageIndex,
      apiResponse,
      dataPathValidationResult,
      parentDataSet,
      parentDataSetSamples,
      dataMaps,
      isLoadingProbeSample,
      isLoadingDataMaps,
      onAPIConfigChanged,
      onGetParameterizedURLs,
      onLoadDataMaps,
      // Render-only suppress flags: when the redesigned step list (RestApiStepList)
      // renders its own popover/tabs for these sections, it hides the legacy inline
      // blocks to avoid two editors writing the same config keys. All default to
      // showing the section, so every other caller/flow type is unaffected.
      hidePaginationSection = false,
      hideResponsePathSection = false,
      hideRequestHeadersSection = false,
      // Render-only: the redesigned step list renders Method + URL + Test in the
      // card header (StepUrlComposer), so it hides this inline row to avoid a
      // duplicate editor. Default off → other callers/flow types unaffected.
      hideUrlSection = false,
      // Render-only: the redesigned URL composer surfaces macro settings (date
      // format / time unit) inside the {-popup and lookups above the steps, so it
      // hides this standing "URL Macros" accordion. Default off.
      hideUrlMacrosSection = false,
      // Render-only: the redesigned URL composer renders its own time-macro-
      // evaluated sample URLs, so it hides the legacy ParameterizedURLPreview.
      hideUrlPreview = false,
      // Render-only: the redesigned step list renders the request body in its own
      // "Request Body" tab, so it hides this inline body editor.
      hideBodySection = false,
      // Render-only: the redesigned layout shows a single global Response Preview
      // panel (outside the cards), so it hides this per-step response viewer.
      hideResponseViewer = false,
      // Render-only: adds a scoped className so the redesigned step list can
      // re-skin the legacy field markup via CSS (no markup/logic change).
      redesignSkin = false,
    } = this.props;

    const { activePanelKey } = this.state;
    const { isPaginated, contentType } = stageAPIConfig;
    const fields = sourceConfigById.rest;
    const fieldsByName = groupBy(fields, 'name');
    const iterationType = stageAPIConfig['iteration.type'];
    const dataMapIds = get(apiConfig, 'dataMapIds', null);
    const dataMapsSelectionValues = map(values(dataMaps), (value) => ({
      label: value.name,
      value: value.id,
    }));
    const pathValidationFailed =
      dataPathValidationResult &&
      !dataPathValidationResult.status &&
      !reduce(
        dataPathValidationResult,
        (status, validationResult) => status && validationResult.status,
        true,
      );
    const pathMatches = this._getPathMatches();

    let apiResponseContent;
    let apiResponseCode;
    let apiResponseMessage;

    if (apiResponse) {
      apiResponseContent = apiResponse.apiResponseContent;
      apiResponseCode = apiResponse.apiResponseCode;
      apiResponseMessage = apiResponse.apiResponseMessage;
    } else {
      apiResponseContent = '';
      apiResponseCode = null;
      apiResponseMessage = '';
    }

    const editorContent =
      !pathValidationFailed && pathMatches !== null ? pathMatches : apiResponseContent;
    const responseFailure = apiResponseCode >= 400;
    const responseHeaderClassName = responseFailure ? 'response-failure' : 'response-success';

    const editorLanguage = getEditorLanguage(contentType);

    const validationResponse = this.props.dataPathValidationResult?.['response.data.path'];
    const showRecordFoundMessage =
      validationResponse?.status || apiResponse?.apiResponseContentLength > 0;
    const recordFoundMessage = showRecordFoundMessage
      ? validationResponse?.status
        ? validationResponse?.message
        : UIUtils.toPluralFormat('record', apiResponse.apiResponseContentLength) + ' found'
      : null;

    return (
      <div
        className={classnames({
          'configure-api-stage': true,
          'configure-api-stage-focused': this.props?.isFocused,
          'configure-api-stage--redesigned': redesignSkin,
        })}
      >
        <section className="configuration-holder">
          <ol>
            <li>
              {!hideUrlSection && (
                <div className="input-row api-url">
                  <div className="input-holder method-input-holder">
                    <FieldLabelView
                      fieldName="method"
                      defaultLabel="Method"
                      labelClassName="source-config source-config-rest-method"
                      fieldsByName={fieldsByName}
                    />
                    {this._getSelectField(
                      'method',
                      fieldsByName.method && fieldsByName.method[0].options,
                      SELECT_FIELD_CUSTOM_STYLES.fixedWidthControl,
                    )}
                  </div>
                  <div className="input-holder-api-url-container">
                    <div className="input-holder api-url-input-holder">
                      <div className="api-url-label-holder">
                        <FieldLabelView
                          fieldName="url.template"
                          fieldsByName={fieldsByName}
                          defaultLabel="Set API URL"
                          labelClassName="source-config source-config-rest-url source-config-rest-url.template"
                        />
                      </div>
                      <div className="api-url-editor-holder" data-testid="api-url-editor">
                        <MacroEditorConfigProvider
                          dateFormat={stageAPIConfig['date.format'] || 'yyyy-MM-dd'}
                          dateTimeUnit={stageAPIConfig['date.time.unit'] || 'dd'}
                          parentDatasetSamples={extractDatasetFromApiServerFlow(
                            parentDataSet,
                            parentDataSetSamples,
                            editedSamples,
                          )}
                          parameterizedURLs={onGetParameterizedURLs(stageIndex)}
                        >
                          <MacroEditor
                            multiline={true}
                            rows={1}
                            maxRows={4}
                            value={stageAPIConfig['url.template']}
                            onChange={this._handleURLTemplateChange}
                            suggestions={this.state.filteredURLSuggestions}
                          />
                        </MacroEditorConfigProvider>
                        {/* {this._getTextField('url.template', {
                      onKeyDown: this._handleCallAPIKeyDown
                    })} */}
                      </div>
                    </div>
                    <div className="call-api-button-container">
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={this._handleCallAPIClicked}
                        className={cx('call-api-button', {
                          loading: isLoadingProbeSample,
                        })}
                        data-testid="call-api-button"
                      >
                        {isLoadingProbeSample ? (
                          <CircularProgress
                            size={19}
                            sx={{
                              color: 'var(--color-danger-700)',
                            }}
                          />
                        ) : (
                          <>Test</>
                        )}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
              {!hideUrlMacrosSection && (
                <div className="data-path-suggestions url-macros-container">
                  <Accordion
                    key={1}
                    square
                    className="accordion-item accordion-item-url-macro"
                    expanded={activePanelKey === 1}
                    onChange={() => this._handlePanelSelected(1)}
                  >
                    <AccordionSummary className="accordion-title" data-testid="url-macros-title">
                      <div className="accordion-title-content">
                        <div style={{ display: 'flex' }}>
                          <Expand
                            className={classnames('expand', {
                              ['expand-open']: activePanelKey === 1,
                              ['expand-close']: activePanelKey !== 1,
                            })}
                          />
                        </div>
                        <h4 className="accordion-content-grow">URL Macros</h4>
                      </div>
                    </AccordionSummary>
                    <AccordionDetails className="accordion-body">
                      <div className="suggestions-container" data-testid="url-macros-info">
                        <p>
                          Customize and add Nexla macros to the API URL and Nexla will automatically
                          convert them into values when executing the API calls. Type{' '}
                          <code>{'{'}</code> in the URL input above to add macros.
                        </p>
                        <p>
                          Use System Date/Time macros to specify relative time like now, yesterday,
                          or tomorrow. You can also specify mathematical operations in macros, for
                          example <code>{'{now-4}'}</code> for 4 days ago (UTC).
                        </p>
                        <div className="input-row">
                          <div className="input-holder">
                            <FieldLabelView
                              fieldName="date.format"
                              fieldsByName={fieldsByName}
                              defaultLabel="Date Format for Date/Time Macro"
                              labelClassName="source-config source-config-rest-label source-config-rest-date.format"
                            />
                            {this._getSelectField(
                              'date.format',
                              fieldsByName['date.format'] && fieldsByName['date.format'][0].options,
                              null,
                              false,
                              {
                                isCreatable: true,
                                formatCreateLabel: identity,
                                placeholder: 'Select or type...',
                              },
                            )}
                          </div>
                          <div className="input-holder">
                            <FieldLabelView
                              fieldName="date.time.unit"
                              fieldsByName={fieldsByName}
                              defaultLabel="Time Unit for Operations"
                              labelClassName="source-config source-config-rest-label source-config-rest-date.time.unit"
                            />
                            {this._getSelectField(
                              'date.time.unit',
                              fieldsByName['date.time.unit'] &&
                                fieldsByName['date.time.unit'][0].options,
                            )}
                          </div>
                        </div>
                        <div className="input-row">
                          <div className="input-holder ">
                            <FieldLabelView
                              fieldName="map.id"
                              fieldsByName={fieldsByName}
                              defaultLabel="Add Lookups to supported macros"
                              labelClassName="source-config source-config-rest-label source-config-rest-map.id"
                            />
                            <div
                              data-testid="data-map-input-holder"
                              data-selected-value={
                                dataMapIds?.map(({ value }) => value).join(',') || ''
                              }
                              className="input-holder-rest-map"
                            >
                              <SortedSelect
                                value={dataMapIds}
                                components={{
                                  Input: SelectInput,
                                  Option: SelectOption,
                                }}
                                isMulti
                                options={dataMapsSelectionValues}
                                isClearable={false}
                                backspaceRemovesValue={false}
                                isSearchable
                                styles={redesignStyles}
                                onChange={this._handleLookupSelectionChanged}
                                className="input-holder-rest-map-select __TEST_field_map-id"
                              />

                              {onLoadDataMaps &&
                                (isLoadingDataMaps ? (
                                  <RefreshIconRotate fontSize="large" className="refresh-icon" />
                                ) : (
                                  <button className="refresh-button" onClick={onLoadDataMaps}>
                                    <img
                                      alt=""
                                      width={16}
                                      height={16}
                                      src="https://cdn.nexla.io/ui/static/icons/refresh.svg"
                                    />
                                  </button>
                                ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </AccordionDetails>
                  </Accordion>
                </div>
              )}
              {!hideUrlPreview && stageAPIConfig['url.template'] && (
                <ParameterizedURLPreview
                  urlTemplate={stageAPIConfig['url.template']}
                  parameterizedURLs={onGetParameterizedURLs(stageIndex)}
                />
              )}
              {!hideBodySection &&
                (stageAPIConfig.method === 'POST' ||
                  stageAPIConfig.method === 'PUT' ||
                  stageAPIConfig.method === 'PATCH') && (
                  <div className="input-row">
                    <div className="input-holder">
                      <FieldLabelView
                        fieldName="body.template"
                        defaultLabel="Request Body"
                        labelClassName="source-config source-config-rest-body-label source-config-rest-body.template"
                        fieldsByName={fieldsByName}
                      />
                      <MacroEditorConfigProvider
                        dateFormat={stageAPIConfig['date.format'] || 'yyyy-MM-dd'}
                        dateTimeUnit={stageAPIConfig['date.time.unit'] || 'dd'}
                        parentDatasetSamples={extractDatasetFromApiServerFlow(
                          parentDataSet,
                          parentDataSetSamples,
                          editedSamples,
                        )}
                      >
                        <MacroEditor
                          value={stageAPIConfig['body.template']}
                          multiline={true}
                          rows={8}
                          maxRows={10}
                          onChange={this._handleBodyTemplateChange}
                          suggestions={this.state.filteredBodySuggestions || []}
                          placeholder="Enter request body (JSON or text with macros)"
                          isJson={true}
                        />
                      </MacroEditorConfigProvider>
                    </div>
                  </div>
                )}
            </li>
            {!hideResponsePathSection && (
              <li>
                {this._getDataPathField(
                  'response.data.path',
                  fieldsByName,
                  'Set path to data in response',
                  'source-config source-config-rest-label source-config-rest-response.data.path',
                )}
                <DataPathSuggestions
                  content={apiResponseContent}
                  contentType={contentType}
                  onSuggestionClicked={this._handleDataPathSuggestionClicked}
                />
                <div className="input-row">
                  <div className="input-holder">
                    <FieldLabelView
                      fieldName="response.data.path.additional"
                      fieldsByName={fieldsByName}
                      defaultLabel="Path to metadata in response"
                      labelClassName="source-config source-config-rest-label source-config-rest-response.data.path.additional"
                    />
                    {this._getTextField('response.data.path.additional', {}, true)}
                  </div>
                </div>
              </li>
            )}
            {!hideRequestHeadersSection && (
              <li>
                <div className="input-row">
                  <div className="input-holder">
                    <FieldLabelView
                      fieldName="request.headers"
                      fieldsByName={fieldsByName}
                      defaultLabel="(Optional) Request Headers"
                      labelClassName="source-config source-config-rest-label source-config-rest-request.headers"
                    />
                    {this._getTextField(
                      'request.headers',
                      {
                        multiline: true,
                        minRows: 4,
                        maxRows: 8,
                      },
                      true,
                    )}
                  </div>
                </div>
              </li>
            )}
            {!hidePaginationSection && (
              <li>
                <div className="check-box-holder" style={{ display: 'flex', alignItems: 'center' }}>
                  <StyledFormControlLabel
                    label="This API is paginated"
                    control={
                      <Checkbox
                        data-testid="is-paginated"
                        checked={isPaginated}
                        className="source-config source-config-rest-allow-iteration"
                        onChange={(event, isChecked) =>
                          onAPIConfigChanged('isPaginated', isChecked, stageIndex)
                        }
                      />
                    }
                  />
                </div>
                {isPaginated && (
                  <div>
                    <div className="input-row">
                      <div className="input-holder">
                        <FieldLabelView
                          fieldName="iteration.type"
                          defaultLabel="Pagination Type"
                          labelClassName="source-config source-config-rest-label source-config-rest-iteration.type"
                          fieldsByName={fieldsByName}
                        />
                        {this._getSelectField(
                          'iteration.type',
                          Constants.IterationTypes,
                          SELECT_FIELD_CUSTOM_STYLES.baseControl,
                        )}
                      </div>
                    </div>
                    {this._getIterationTypeFields(iterationType, fieldsByName)}
                  </div>
                )}
              </li>
            )}
          </ol>
        </section>
        {!hideResponseViewer && (
          <section className="api-response-holder" data-testid="api-response-holder">
            <header className={responseHeaderClassName}>
              <div className="source-config-rest-api-response-container">
                <span
                  className="source-config source-config-rest-api-response-title"
                  data-testid="api-response-title"
                >
                  Endpoint Test Result
                </span>

                {showRecordFoundMessage ? (
                  <span
                    data-testid="api-response-success"
                    className="source-config-rest-api-response-success"
                  >
                    {recordFoundMessage}
                  </span>
                ) : null}

                {responseFailure && (
                  <span className="source-config-rest-api-response-fail">
                    Failure: {apiResponseMessage} (Status Code: {apiResponseCode})
                  </span>
                )}
              </div>
            </header>
            <LoadableMonacoEditor
              language={editorLanguage}
              theme="dark"
              value={editorContent}
              options={{
                readOnly: true,
                minimap: { enabled: false },
              }}
            />
          </section>
        )}
      </div>
    );
  }

  _handlePanelSelected(panelKey) {
    const nextKey = panelKey !== this.state.activePanelKey ? panelKey : null;

    this.setState({ activePanelKey: nextKey });
  }

  _handleCallAPIClicked() {
    const { stageIndex, onCallAPI } = this.props;

    onCallAPI(stageIndex);
  }

  _handleCallAPIKeyDown(event) {
    if (event.keyCode === 13) {
      this._handleCallAPIClicked();
    }
  }

  _handleLookupSelectionChanged(value, data) {
    const { apiConfig, setFormError, onAPIConfigChanged } = this.props;
    let iterationWithRemovedDataMap = null;

    if (data.action === 'remove-value') {
      const dataMapId = data.removedValue.value;
      const dataMapMacro = `{lookup${dataMapId}.`;

      iterationWithRemovedDataMap = find(apiConfig['rest.iterations'], (iteration) =>
        includes(iteration['url.template'], dataMapMacro),
      );
    }

    if (iterationWithRemovedDataMap) {
      setFormError(
        FORM_TYPE_CREATE_DATA_SOURCE,
        `This lookup is used in the API URL ${iterationWithRemovedDataMap['url.template']}.`,
      );
    } else {
      const newMaps = !isUndefined(value) ? value : null;

      onAPIConfigChanged('dataMapIds', newMaps);
    }
  }

  _getPathMatches() {
    const {
      dataPathValidationResult,
      stageAPIConfig: { contentType },
    } = this.props;
    const matches = get(dataPathValidationResult, '["response.data.path"].matches');

    if (!matches) {
      return null;
    }

    const matchesString = stringifyObject(matches, contentType);

    return formatCode(matchesString, contentType);
  }

  _getDataPathField(fieldName, fieldsByName, label, labelClassName) {
    const { stageAPIConfig, apiResponse, dataPathValidationResult } = this.props;
    const { contentType } = stageAPIConfig;
    const dataPath = stageAPIConfig[fieldName];
    const pathValidationFailed =
      dataPathValidationResult &&
      !dataPathValidationResult.status &&
      dataPathValidationResult[fieldName] &&
      !dataPathValidationResult[fieldName].status;
    const message =
      dataPathValidationResult &&
      dataPathValidationResult[fieldName] &&
      dataPathValidationResult[fieldName].message;

    const fieldInfo = !isEmpty(fieldsByName[fieldName]) ? fieldsByName[fieldName][0] : null;

    const labelView =
      fieldInfo && !isEmpty(fieldInfo.description) ? (
        <Tooltip
          arrow={true}
          position="right"
          tooltipTheme="fieldInfo"
          title={<MarkdownContent>{fieldInfo.description}</MarkdownContent>}
        >
          <label className={labelClassName} htmlFor={fieldName} style={{ cursor: 'pointer' }}>
            {fieldInfo.display_name}&nbsp;
            {contentType === 'application/xml' && '(XPath expression)'}
            {contentType === 'application/json' && '(JSONPath expression)'}
            <InfoIcon />
          </label>
        </Tooltip>
      ) : (
        <label className={labelClassName} htmlFor={fieldName}>
          {label}&nbsp;
          {contentType === 'application/xml' && '(XPath expression)'}
          {contentType === 'application/json' && '(JSONPath expression)'}
        </label>
      );

    return (
      <div className="input-row">
        <div className="input-holder">
          <div className="data-path-validation-result-holder">{labelView}</div>
          <div className="data-path-input-holder">{this._getTextField(fieldName, {}, true)}</div>
          {apiResponse && dataPathValidationResult && dataPath && (
            <div
              className={classnames('data-path-validation-result', {
                'invalid-data-path': pathValidationFailed,
              })}
            >
              {message}
            </div>
          )}
        </div>
      </div>
    );
  }

  _getIterationTypeFields(iterationType, fieldsByName) {
    const { stageAPIConfig, stageIndex, onAPIConfigChanged } = this.props;

    switch (iterationType) {
      case 'paging.incrementing':
        return (
          <div>
            <div className="input-row">
              <div className="input-holder">
                <FieldLabelView
                  fieldName="param.id"
                  fieldsByName={fieldsByName}
                  defaultLabel="URL Param For Page No."
                  labelClassName="source-config source-config-rest-label source-config-rest-paging.incrementing source-config-rest-param.id"
                />
                {this._getTextField('param.id', {}, true)}
              </div>
              <div className="input-holder">
                <FieldLabelView
                  fieldName="start.page.from"
                  fieldsByName={fieldsByName}
                  defaultLabel="Start From Page"
                  labelClassName="source-config source-config-rest-label source-config-rest-paging.incrementing source-config-rest-start.page.from"
                />
                {this._getTextField('start.page.from', {}, true)}
              </div>
            </div>
            <div className="input-row">
              <div className="input-holder">
                <FieldLabelView
                  fieldName="param.page.size"
                  fieldsByName={fieldsByName}
                  defaultLabel="URL Param For Items Per Page"
                  labelClassName="source-config source-config-rest-label source-config-rest-paging.incrementing source-config-rest-param.page.size"
                />
                {this._getTextField('param.page.size', {}, true)}
              </div>
              <div className="input-holder">
                <FieldLabelView
                  fieldName="page.expected.rows"
                  fieldsByName={fieldsByName}
                  defaultLabel="Items Per Page"
                  labelClassName="source-config source-config-rest-label source-config-rest-paging.incrementing source-config-rest-page.expected.rows"
                />
                {this._getTextField('page.expected.rows', {}, true)}
              </div>
            </div>
            <div className="input-row">
              <div className="input-holder">
                <FieldLabelView
                  fieldName="end.page.to"
                  fieldsByName={fieldsByName}
                  defaultLabel="(Optional) Stop After Page No."
                  labelClassName="source-config source-config-rest-label source-config-rest-paging.incrementing source-config-rest-end.page.to"
                />
                {this._getTextField('end.page.to', {}, true)}
              </div>
              <div className="input-holder">
                <FieldLabelView
                  fieldName="request.parallelism.count"
                  fieldsByName={fieldsByName}
                  defaultLabel="Parallel requests"
                  labelClassName="source-config source-config-rest-label source-config-rest-paging.incrementing source-config-rest-request.parallelism.count"
                />
                {this._getTextField('request.parallelism.count', {}, true)}
              </div>
            </div>
          </div>
        );
      case 'paging.incrementing.offset':
        return (
          <div>
            <div className="input-row">
              <div className="input-holder">
                <FieldLabelView
                  fieldName="param.offset"
                  defaultLabel="URL Param For Offset"
                  labelClassName="source-config source-config-rest-label source-config-rest-paging.incrementing.offset source-config-rest-param.offset"
                  fieldsByName={fieldsByName}
                />
                {this._getTextField('param.offset', {}, true)}
              </div>
              <div className="input-holder">
                <FieldLabelView
                  fieldName="start.offset.from"
                  defaultLabel="Start From Offset"
                  labelClassName="source-config source-config-rest-label source-config-rest-paging.incrementing.offset source-config-rest-start.offset.from"
                  fieldsByName={fieldsByName}
                />
                {this._getTextField('start.offset.from', {}, true)}
              </div>
            </div>
            <div className="input-row">
              <div className="input-holder">
                <FieldLabelView
                  fieldName="param.page.size"
                  defaultLabel="URL Param For Items Per Page"
                  labelClassName="source-config source-config-rest-label source-config-rest-paging.incrementing.offset source-config-rest-param.page.size"
                  fieldsByName={fieldsByName}
                />
                {this._getTextField('param.page.size', {}, true)}
              </div>
              <div className="input-holder">
                <FieldLabelView
                  fieldName="page.expected.rows"
                  defaultLabel="Items Per Page"
                  labelClassName="source-config source-config-rest-label source-config-rest-paging.incrementing.offset source-config-rest-page.expected.rows"
                  fieldsByName={fieldsByName}
                />
                {this._getTextField('page.expected.rows', {}, true)}
              </div>
              <div className="input-holder">
                <FieldLabelView
                  fieldName="end.offset.to"
                  defaultLabel="(Optional) Stop After Offset"
                  labelClassName="source-config source-config-rest-label source-config-rest-paging.incrementing.offset source-config-rest-end.offset.to"
                  fieldsByName={fieldsByName}
                />
                {this._getTextField('end.offset.to', {}, true)}
              </div>
            </div>
          </div>
        );
      case 'paging.next.token':
        return (
          <div>
            {this._getDataPathField(
              'response.next.token.data.path',
              fieldsByName,
              'Path to next token',
              'source-config source-config-rest-label  source-config-rest-paging.next.token source-config-rest-response.next.token.data.path',
            )}
            <div className="input-row">
              <div className="input-holder">
                <FieldLabelView
                  fieldName="param.id"
                  defaultLabel="Token param name"
                  labelClassName="source-config source-config-rest-label source-config-rest-paging.next.token source-config-rest-param.id"
                  fieldsByName={fieldsByName}
                />
                {this._getTextField('param.id', {}, true)}
              </div>
              <div className="input-holder">
                <FieldLabelView
                  fieldName="end.token.to"
                  defaultLabel="(Optional) Stop After Token"
                  labelClassName="source-config source-config-rest-label source-config-rest-paging.next.token source-config-rest-end.token.to"
                  fieldsByName={fieldsByName}
                />
                {this._getTextField('end.token.to', {}, true)}
              </div>
            </div>
          </div>
        );

      case 'paging.next.url':
        return (
          <div>
            {this._getDataPathField(
              'response.next.url.data.path',
              fieldsByName,
              'Path to next URL',
              'source-config source-config-rest-label source-config-rest-paging.next.url source-config-rest-response.next.url.data.path',
            )}
            <div className="input-row">
              <div className="input-holder">
                <FieldLabelView
                  fieldName="end.url.to"
                  defaultLabel="(Optional) Stop After URL"
                  labelClassName="source-config source-config-rest-label source-config-rest-paging.next.url source-config-rest-end.url.to"
                  fieldsByName={fieldsByName}
                />
                {this._getTextField('end.url.to', {}, true)}
              </div>
            </div>
          </div>
        );

      case 'link.header':
        return (
          <div>
            <div className="input-row">
              <div className="input-holder">
                <FieldLabelView
                  fieldName="link"
                  defaultLabel="Response Header containing Next Page URL"
                  labelClassName="source-config source-config-rest-label source-config-rest-link.header source-config-rest-link"
                  fieldsByName={fieldsByName}
                />
                {this._getTextField('link', {}, true)}
              </div>
            </div>
          </div>
        );

      case 'response.id.number':
        return (
          <div className="input-row">
            <div className="input-holder">
              <FieldLabelView
                fieldName="response.id.field.name"
                defaultLabel="ID Name"
                labelClassName="source-config source-config-rest-label source-config-rest-response.id.number source-config-rest-response.id.field.name"
                fieldsByName={fieldsByName}
              />
              {this._getTextField('response.id.field.name', {}, true)}
            </div>
            <div className="input-holder">
              <FieldLabelView
                fieldName="start.id.from"
                defaultLabel="Start ID"
                labelClassName="source-config source-config-rest-label source-config-rest-response.id.number source-config-rest-start.id.from"
                fieldsByName={fieldsByName}
              />
              {this._getTextField('start.id.from', {}, true)}
            </div>
            <div className="input-holder">
              <FieldLabelView
                fieldName="param.id"
                defaultLabel="URL ID Param"
                labelClassName="source-config source-config-rest-label source-config-rest-response.id.number source-config-rest-param.id"
                fieldsByName={fieldsByName}
              />
              {this._getTextField('param.id', {}, true)}
            </div>
            <div className="input-holder">
              <FieldLabelView
                fieldName="param.id.inclusive"
                defaultLabel="Next ID inclusive?"
                labelClassName="source-config source-config-rest-label source-config-rest-response.id.number source-config-rest-param.id.inclusive"
                fieldsByName={fieldsByName}
              />
              <StyledCheckbox
                data-testid="testid-param.id.inclusive"
                checked={stageAPIConfig['param.id.inclusive'] || false}
                disableRipple={true}
                style={{
                  display: 'block',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  paddingTop: 0,
                  paddingLeft: 0,
                }}
                onChange={(event, isChecked) =>
                  onAPIConfigChanged('param.id.inclusive', isChecked, stageIndex)
                }
              />
            </div>
            <div className="input-holder">
              <FieldLabelView
                fieldName="end.id.to"
                defaultLabel="(Optional) Stop After ID"
                labelClassName="source-config source-config-rest-label source-config-rest-response.id.number source-config-rest-end.id.to"
                fieldsByName={fieldsByName}
              />
              {this._getTextField('end.id.to', {}, true)}
            </div>
          </div>
        );

      case 'response.id.string':
        return (
          <div className="input-row">
            <div className="input-holder">
              <FieldLabelView
                fieldName="response.id.field.name"
                defaultLabel="ID Name"
                labelClassName="source-config source-config-rest-label source-config-rest-response.id.string source-config-rest-response.id.field.name"
                fieldsByName={fieldsByName}
              />
              {this._getTextField('response.id.field.name', {}, true)}
            </div>
            <div className="input-holder">
              <FieldLabelView
                fieldName="start.id.from"
                defaultLabel="Start ID from"
                labelClassName="source-config source-config-rest-label source-config-rest-response.id.string source-config-rest-start.id.from"
                fieldsByName={fieldsByName}
              />
              {this._getTextField('start.id.from', {}, true)}
            </div>
            <div className="input-holder">
              <FieldLabelView
                fieldName="param.id"
                defaultLabel="URL ID Param"
                labelClassName="source-config source-config-rest-label source-config-rest-response.id.string source-config-rest-param.id"
                fieldsByName={fieldsByName}
              />
              {this._getTextField('param.id', {}, true)}
            </div>
            <div className="input-holder">
              <FieldLabelView
                fieldName="end.id.to"
                defaultLabel="(Optional) Stop After ID"
                labelClassName="source-config source-config-rest-label source-config-rest-response.id.string source-config-rest-end.id.to"
                fieldsByName={fieldsByName}
              />
              {this._getTextField('end.id.to', {}, true)}
            </div>
          </div>
        );

      case 'graphql.page':
        return (
          <div>
            <div className="input-row">
              <div className="input-holder">
                <FieldLabelView
                  fieldName="graphql.page.name"
                  defaultLabel="Page Variable Name"
                  labelClassName="source-config source-config-rest-label source-config-rest-graphql.page source-config-rest-graphql.page.name"
                  fieldsByName={fieldsByName}
                />
                {this._getTextField('graphql.page.name', {}, true)}
              </div>
              <div className="input-holder">
                <FieldLabelView
                  fieldName="graphql.page.start"
                  defaultLabel="Starting Page Number"
                  labelClassName="source-config source-config-rest-label source-config-rest-graphql.page source-config-rest-graphql.page.start"
                  fieldsByName={fieldsByName}
                />
                {this._getTextField('graphql.page.start', {}, true)}
              </div>
            </div>
          </div>
        );

      case 'graphql.cursor':
        return (
          <div>
            <div className="input-row">
              <div className="input-holder">
                <FieldLabelView
                  fieldName="graphql.cursor.name"
                  defaultLabel="Cursor Variable Name"
                  labelClassName="source-config source-config-rest-label source-config-rest-graphql.cursor source-config-rest-graphql.cursor.name"
                  fieldsByName={fieldsByName}
                />
                {this._getTextField('graphql.cursor.name', {}, true)}
              </div>
            </div>
            {this._getDataPathField(
              'response.graphql.cursor.path',
              fieldsByName,
              'Path to Next Cursor',
              'source-config source-config-rest-label  source-config-rest-graphql.cursor source-config-rest-response.graphql.cursor.path',
            )}
            {stageIndex > 0 && (
              <div className="input-row">
                <div className="input-holder">
                  <FieldLabelView
                    fieldName="graphql.cursor.template"
                    defaultLabel="Initial Cursor Source"
                    labelClassName="source-config source-config-rest-label source-config-rest-graphql.cursor source-config-rest-graphql.cursor.template"
                    fieldsByName={fieldsByName}
                  />
                  {this._getTextField('graphql.cursor.template', {}, true)}
                </div>
                <div className="input-holder">
                  <FieldLabelView
                    fieldName="results.pass.through"
                    defaultLabel="Include Previous Step Results"
                    labelClassName="source-config source-config-rest-label source-config-rest-graphql.cursor source-config-rest-graphql.cursor.template.type"
                    fieldsByName={fieldsByName}
                  />
                  <StyledCheckbox
                    data-testid="testid-results.pass.through"
                    checked={stageAPIConfig['results.pass.through'] || false}
                    disableRipple={true}
                    style={{
                      display: 'block',
                      alignItems: 'center',
                      justifyContent: 'flex-start',
                      paddingTop: 0,
                      paddingLeft: 0,
                    }}
                    onChange={(event, isChecked) =>
                      onAPIConfigChanged('results.pass.through', isChecked, stageIndex)
                    }
                  />
                </div>
              </div>
            )}
          </div>
        );

      case 'async.poll':
        return (
          <div className="input-row">
            <div className="input-holder">
              <FieldLabelView
                fieldName="async.iteration.path"
                defaultLabel="Job Status Param"
                labelClassName="source-config source-config-rest-label source-config-rest-async.poll source-config-rest-async.iteration.path"
                fieldsByName={fieldsByName}
              />
              {this._getTextField('async.iteration.path', {}, true)}
            </div>
            <div className="input-holder">
              <FieldLabelView
                fieldName="async.iteration.value"
                defaultLabel="Job Completion Value"
                labelClassName="source-config source-config-rest-label source-config-rest-async.poll source-config-rest-async.iteration.value"
                fieldsByName={fieldsByName}
              />
              {this._getTextField('async.iteration.value', {}, true)}
            </div>
          </div>
        );

      case 'body.as.file':
        return (
          <div className="input-row">
            <div className="input-holder">
              <FieldLabelView
                fieldName="file.response.format"
                defaultLabel="Data Format of Response File"
                labelClassName="source-config source-config-rest-label source-config-rest-body.as.file source-config-rest-file.response.format"
                fieldsByName={fieldsByName}
              />
              {this._getSelectField('file.response.format', [
                { label: 'CSV', value: 'csv' },
                { label: 'JSON', value: 'json' },
                { label: 'TXT', value: 'txt' },
                { label: 'XML', value: 'xml' },
              ])}
            </div>
          </div>
        );

      default:
        return null;
    }
  }

  /**
   * TODO: replace by FieldBuilder
   */
  _getTextField(name, props = {}, useMacros = false) {
    const {
      stageAPIConfig,
      stageIndex,
      onAPIConfigChanged,
      parentDataSet,
      parentDataSetSamples,
      onGetParameterizedURLs,
      apiConfig,
      isAPIServerTarget,
      editedSamples = null,
    } = this.props;

    // Determine if we should use MacroEditor
    const shouldUseMacros = useMacros && isAPIServerTarget;

    if (shouldUseMacros) {
      return (
        <MacroEditorConfigProvider
          dateFormat={stageAPIConfig['date.format'] || 'yyyy-MM-dd'}
          dateTimeUnit={stageAPIConfig['date.time.unit'] || 'dd'}
          parentDatasetSamples={extractDatasetFromApiServerFlow(
            parentDataSet,
            parentDataSetSamples,
            editedSamples,
          )}
          parameterizedURLs={onGetParameterizedURLs(stageIndex)}
        >
          <MacroEditor
            multiline={props.multiline || false}
            rows={props.rows || 1}
            maxRows={props.maxRows}
            value={stageAPIConfig[name] || ''}
            onChange={(newValue) => onAPIConfigChanged(name, trim(newValue), stageIndex)}
            suggestions={this.state.filteredURLSuggestions}
            placeholder={props.placeholder}
          />
        </MacroEditorConfigProvider>
      );
    }

    return (
      <DebouncedText
        id={name}
        data-testid={`testid-${name}`}
        value={stageAPIConfig[name] || ''}
        onChange={(text) => onAPIConfigChanged(name, trim(text), stageIndex)}
        {...props}
      />
    );
  }

  /**
   * TODO: replace by FieldBuilder
   */
  _getSelectField(name, options, controlStyleOptions = null, sort = false, customProps = {}) {
    const { stageAPIConfig, stageIndex, onAPIConfigChanged } = this.props;
    const currentValue = getCurrentSelectedOption(
      options,
      stageAPIConfig[name],
      null,
      null,
      !!customProps.isCreatable,
    );
    const selectProps = {
      instanceId: name,
      value: currentValue,
      styles: redesignStyles,
      options: options,
      components: { Input: SelectInput, Option: SelectOption },
      isClearable: false,
      backspaceRemovesValue: false,
      isSearchable: true,
      className: `select-field __TEST_field_${name.replaceAll('.', '-')}`,
      onChange: (currValue) => {
        let newValue = getNewSelectionValue(currValue, true);
        onAPIConfigChanged(name, newValue, stageIndex);
      },
      ...customProps,
    };

    return sort === true ? <SortedSelect {...selectProps} /> : <UnsortedSelect {...selectProps} />;
  }

  _handleURLKeyDown(event, editorState) {
    const { stageIndex, onAPIConfigChanged } = this.props;

    let urlTemplate = this._saveURLTemplate(editorState);
    onAPIConfigChanged('url.template', urlTemplate.trim(), stageIndex);
    //TODO: forced hack..delay the callAPI method so that the parent's url template has been updated.
    setTimeout(() => {
      this._handleCallAPIClicked();
    }, 10);
  }

  _handleURLTemplateEditorChange(editorState) {
    if (editorState.getCurrentContent() !== this.state.urlTemplateEditorState.getCurrentContent()) {
      // content has changed
      this._saveURLTemplateDebounced(editorState);
    }

    this.setState({
      urlTemplateEditorState: editorState,
    });
  }

  _handleURLTemplateEditorSearch(suggestions) {
    this.setState({
      filteredURLSuggestions: suggestions,
    });
  }

  // for new macro editor
  _handleURLTemplateChange = (newValue) => {
    this.setState({
      urlTemplate: newValue,
    });
    this.props.onAPIConfigChanged('url.template', newValue.trim(), this.props.stageIndex);
  };

  // for new macro editor
  _handleBodyTemplateChange = (newValue) => {
    this.setState({
      bodyTemplate: newValue,
    });
    this.props.onAPIConfigChanged('body.template', newValue.trim(), this.props.stageIndex);
  };

  _handleDataPathSuggestionClicked(dataPath) {
    const { stageIndex, onAPIConfigChanged } = this.props;

    onAPIConfigChanged('response.data.path', dataPath, stageIndex);
  }

  //NEX-2489: No need to debounce anymore with draft js plugin upgrade.
  // _saveURLTemplateDebounced = debounce((editorState) => {
  //   let urlTemplate = this._saveURLTemplate(editorState);
  //   this.props.onAPIConfigChanged('url.template', urlTemplate.trim());
  // }, 1000);
  _saveURLTemplateDebounced = (editorState) => {
    const { stageIndex, onAPIConfigChanged } = this.props;

    let urlTemplate = this._saveURLTemplate(editorState);
    onAPIConfigChanged('url.template', urlTemplate.trim(), stageIndex);
  };

  _saveURLTemplate = (editorState) => {
    const { macroMap, stageURLMacros } = this.state;
    const contentState = editorState.getCurrentContent();

    let { payloadText } = extractPayloadFromMentionContent(
      contentState,
      DEFAULT_MACRO_ENTITY_NAME,
      true,
      stageURLMacros,
      macroMap,
    );

    return payloadText;
  };
}

ConfigureAPIStage.defaultProps = {
  apiURLMacros: {},
  macrosUpdateTime: null,
};

export default ConfigureAPIStage;
